<script setup lang="ts">
import { computed, type PropType, ref } from "vue";
import { DesignVersion, Placement, RadioCheckmark } from "@/models/types";

defineOptions({ name: "BcRadioButton" });

/** Specifies two-way binding for the radio button */
const modelValue = defineModel<any>({ default: undefined });

const props = defineProps({
  /** Specifies the radio button's checkmark style */
  checkmark: {
    type: String as PropType<RadioCheckmark>,
    default: "circle",
    validator: (value: any) => RadioCheckmark.isRadioCheckmark(value),
  },
  /** Specifies the radio button's style */
  design: {
    type: String as PropType<DesignVersion>,
    default: "6",
    validator: (value: any) => DesignVersion.isDesignVersion(value),
  },
  /** Disables radio button for selection */
  disabled: { type: Boolean, default: false },
  /** Specifies the label of the radio button */
  label: { type: [String, Number, Boolean], default: undefined },
  /** Specifies the position of the radio button's label */
  labelPlacement: {
    type: String as PropType<Placement>,
    default: "right",
    validator: (value: any) => Placement.isPlacement(value),
  },
  /** Specifies the name of the radio button */
  name: { type: String },
  /** Specifies that radio button is read-only */
  readonly: { type: Boolean, default: false },
  /** Specifies the value of the radio button */
  value: { type: [String, Number, Boolean, Function, Object, Array] },
});

const emit = defineEmits<{
  (e: "change", payload: any): void;
  (e: "update:model-value", payload: any): void;
}>();

const containerRef = ref<HTMLLabelElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);

const isChecked = computed(() => modelValue.value === props.value);

function focus(): void {
  // MacOS FireFox and Safari do not focus when clicked
  inputRef.value?.focus();
}

const pak = computed<string>(() => {
  return "bc-controls-d" + props.design;
});

function updateInput(event: Event): void {
  const radioInput = event.target as HTMLInputElement;

  switch (typeof props.value) {
    case "boolean":
      modelValue.value = radioInput.value === "true";
      emit("change", radioInput.value === "true");
      break;
    case "number":
      modelValue.value = Number.parseFloat(radioInput.value);
      emit("change", Number.parseFloat(radioInput.value));
      break;
    default:
      modelValue.value = radioInput.value;
      emit("change", radioInput.value);
      break;
  }
}

defineExpose({
  modelValue,
  containerRef,
  inputRef,
  isChecked,
});
</script>

<template>
  <label
    class="bissantzControls radiobuttonControl"
    :data-pak="pak"
    :class="[
      `label-${labelPlacement}`,
      `checkmark-${checkmark}`,
      {
        disabled: disabled,
        readonly: readonly,
      },
    ]"
    ref="containerRef"
    :data-design="design"
    :disabled="disabled"
    @click="focus"
    @keydown.prevent.enter="containerRef?.click()"
  >
    <input
      v-show="!readonly"
      type="radio"
      ref="inputRef"
      @click.stop
      :disabled="disabled"
      :readonly="readonly"
      :name="name"
      :checked="isChecked"
      :value="value"
      @change="updateInput"
    />
    <span
      class="radioButtonLabel"
      :class="{ readonly: readonly }"
      :data-locid="$attrs['data-label-locid']"
      :data-locparam="$attrs['data-label-locparam']"
      :data-locparam1="$attrs['data-label-locparam1']"
      :data-locparam2="$attrs['data-label-locparam2']"
      ><slot>{{ label }}</slot></span
    >
  </label>
</template>

<style lang="less">
@import "../styles/components/bc-radio-button.less";
</style>
